import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, X, Search, Trash2, Download, CheckCircle2, Settings as SettingsIcon, Upload as UploadIcon } from "lucide-react";
import {
  fetchInvoices,
  fetchInvoiceSettings,
  updateInvoiceSettings,
  createOutgoingInvoice,
  createIncomingInvoice,
  deleteInvoice,
  markInvoicePaid,
  computeInvoiceTotals,
  fetchServices,
  createService,
  updateService,
  deleteService,
} from "../lib/invoiceEngine";
import { fetchArtists, fetchCustomers, fetchExternals } from "../lib/driveClient";
import { supabase } from "../../lib/supabase";

const STATUS_COLOR = {
  draft: "text-zinc-400 border-white/15",
  sent: "text-yellow-400 border-yellow-500/40",
  paid: "text-emerald-400 border-emerald-500/40",
  overdue: "text-[#ff0033] border-[#ff0033]/40",
  cancelled: "text-zinc-600 border-white/10",
};
const STATUS_LABEL = { draft: "Entwurf", sent: "Versendet", paid: "Bezahlt", overdue: "Überfällig", cancelled: "Storniert" };

export default function DriveInvoices() {
  const [tab, setTab] = useState("ausgang");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [services, setServices] = useState([]);
  const [artists, setArtists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [externals, setExternals] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, a, c, e, sv] = await Promise.all([
        fetchInvoices({ kind: tab }),
        fetchArtists().catch(() => []),
        fetchCustomers().catch(() => []),
        fetchExternals().catch(() => []),
        fetchServices().catch(() => []),
      ]);
      setRows(inv);
      setArtists(a); setCustomers(c); setExternals(e); setServices(sv);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const filtered = rows.filter(r =>
    q ? (
      r.invoice_number?.toLowerCase().includes(q.toLowerCase()) ||
      r.drive_artists?.artist_name?.toLowerCase().includes(q.toLowerCase()) ||
      r.drive_customers?.name?.toLowerCase().includes(q.toLowerCase()) ||
      r.drive_externals?.full_name?.toLowerCase().includes(q.toLowerCase())
    ) : true
  );

  const download = async (inv) => {
    try {
      if (!inv.storage_path) throw new Error("Kein PDF vorhanden");
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(inv.storage_path, 60, { download: `Rechnung-${inv.invoice_number}.pdf` });
      if (error) throw error;
      const a = document.createElement("a");
      a.href = data.signedUrl; a.download = `Rechnung-${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(e.message); }
  };

  const preview = async (inv) => {
    try {
      if (!inv.storage_path) throw new Error("Kein PDF vorhanden");
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(inv.storage_path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.message); }
  };

  const doDelete = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich löschen?`)) return;
    try { await deleteInvoice(inv.id); toast.success("Gelöscht"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const doMarkPaid = async (inv) => {
    try { await markInvoicePaid(inv.id); toast.success("Als bezahlt markiert"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
            <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
            Buchhaltung
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-5xl">Rechnungen</h1>
          <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
            Erstelle Ausgangsrechnungen mit automatischer Nummer, Kundendaten und Fußzeile. Uploade Eingangsrechnungen zur Ablage.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowServices(true)} data-testid="inv-services" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase">
            <Plus size={13} /> Leistungen
          </button>
          <button onClick={() => setShowSettings(true)} data-testid="inv-settings" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-4 py-3 text-[10px] font-bold tracking-[0.25em] uppercase">
            <SettingsIcon size={13} /> Firmendaten
          </button>
          {tab === "ausgang" && (
            <button onClick={() => setShowWizard(true)} data-testid="inv-new" className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase">
              <Plus size={13} /> Rechnung erstellen
            </button>
          )}
          {tab === "eingang" && (
            <button onClick={() => setShowUpload(true)} data-testid="inv-upload" className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#ff0033] hover:text-white px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase">
              <UploadIcon size={13} /> Eingangsrechnung
            </button>
          )}
        </div>
      </motion.div>

      <div className="flex items-center gap-1 border-b border-white/[0.06]">
        <TabBtn active={tab==="ausgang"} onClick={() => setTab("ausgang")} testid="tab-ausgang">Ausgang</TabBtn>
        <TabBtn active={tab==="eingang"} onClick={() => setTab("eingang")} testid="tab-eingang">Eingang</TabBtn>
      </div>

      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechnungs-Nr., Name…"
          className="w-full bg-[#0A0A0A] border border-white/10 pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#ff0033]/50" />
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-10 text-center text-sm text-zinc-500">
          Noch keine {tab === "ausgang" ? "Ausgangs" : "Eingangs"}rechnungen.
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
          {filtered.map(inv => (
            <div key={inv.id} data-testid={`inv-${inv.id}`} className="p-4 md:px-6 grid md:grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center">
              <FileSpreadsheet size={16} className="text-[#ff0033]" />
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">
                  {inv.invoice_number}
                  {" · "}
                  <span className="text-zinc-400">{inv.drive_artists?.artist_name || inv.drive_customers?.name || inv.drive_externals?.full_name || "—"}</span>
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-0.5">
                  {new Date(inv.invoice_date).toLocaleDateString("de-DE")} · {formatMoney(inv.total)} {inv.currency}
                </div>
              </div>
              <span className={`text-[9px] tracking-[0.3em] uppercase px-2 py-1 border ${STATUS_COLOR[inv.status]||""}`}>{STATUS_LABEL[inv.status]||inv.status}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => preview(inv)} title="Vorschau" className="p-2 text-zinc-500 hover:text-white hover:bg-white/5"><FileSpreadsheet size={13} /></button>
                <button onClick={() => download(inv)} title="Download" data-testid={`inv-download-${inv.id}`} className="p-2 text-zinc-500 hover:text-white hover:bg-white/5"><Download size={13} /></button>
                {inv.status !== "paid" && (
                  <button onClick={() => doMarkPaid(inv)} title="Als bezahlt markieren" data-testid={`inv-paid-${inv.id}`} className="p-2 text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/5"><CheckCircle2 size={13} /></button>
                )}
                <button onClick={() => doDelete(inv)} title="Löschen" className="p-2 text-zinc-500 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08]"><Trash2 size={13} /></button>
              </div>
              <div />
            </div>
          ))}
        </div>
      )}

      {showWizard && <InvoiceWizard artists={artists} customers={customers} externals={externals} services={services} onClose={() => setShowWizard(false)} onDone={async () => { setShowWizard(false); await load(); }} />}
      {showUpload && <IncomingUpload artists={artists} customers={customers} externals={externals} onClose={() => setShowUpload(false)} onDone={async () => { setShowUpload(false); await load(); }} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showServices && <ServicesModal services={services} onClose={() => setShowServices(false)} onChange={load} />}
    </div>
  );
}

const TabBtn = ({ active, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid} className={`px-5 py-3 text-[11px] font-bold tracking-[0.25em] uppercase border-b-2 -mb-px ${active ? "border-[#ff0033] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
    {children}
  </button>
);

function formatMoney(v) {
  return (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

// ─── Wizard: neue Ausgangsrechnung ─────────────────────────
const InvoiceWizard = ({ artists, customers, externals, services, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState(""); // artist | customer | external
  const [artistId, setArtistId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [externalId, setExternalId] = useState("");
  const [items, setItems] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [serviceDate, setServiceDate] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState("");
  const [taxRate, setTaxRate] = useState(19);
  const [busy, setBusy] = useState(false);

  const partySelected = artistId || customerId || externalId;
  const { subtotal, tax_amount, total } = computeInvoiceTotals(items, taxRate);

  const addPreset = (p) => setItems(prev => [...prev, { description: p.label, quantity: 1, unit: p.unit, unit_price: p.price }]);
  const addCustom = () => setItems(prev => [...prev, { description: "", quantity: 1, unit: "", unit_price: 0 }]);
  const updateItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (items.length === 0) { toast.error("Mindestens eine Position hinzufügen"); return; }
    setBusy(true);
    try {
      await createOutgoingInvoice({
        artist_id: artistId || null,
        customer_id: customerId || null,
        external_id: externalId || null,
        invoice_date: invoiceDate,
        service_date: serviceDate || null,
        due_date: dueDate || null,
        items: items.map(it => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })),
        tax_rate: Number(taxRate),
        notes,
      });
      toast.success("Rechnung erstellt");
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Schritt {step} / 3</div>
            <div className="font-display font-black uppercase text-lg mt-1">Neue Rechnung</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Rechnungsempfänger</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: "artist", l: "Künstler" }, { k: "customer", l: "Kunde" }, { k: "external", l: "Extern" }
                ].map(o => (
                  <button key={o.k} onClick={() => { setKind(o.k); setArtistId(""); setCustomerId(""); setExternalId(""); }} data-testid={`inv-kind-${o.k}`}
                    className={`p-3 text-[10px] font-bold tracking-[0.2em] uppercase border transition-colors ${kind===o.k ? "border-[#ff0033] bg-[#ff0033]/[0.05] text-white" : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              {kind === "artist" && (
                <Field label="Künstler auswählen">
                  <select value={artistId} onChange={(e) => setArtistId(e.target.value)} data-testid="inv-artist" className="wiz-input">
                    <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                    {artists.map(a => <option key={a.id} value={a.id} className="bg-[#0A0A0A]">{a.artist_name}</option>)}
                  </select>
                </Field>
              )}
              {kind === "customer" && (
                <Field label="Kunde auswählen">
                  <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="inv-customer" className="wiz-input">
                    <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                    {customers.map(c => <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.name}</option>)}
                  </select>
                </Field>
              )}
              {kind === "external" && (
                <Field label="Externen Kontakt auswählen">
                  <select value={externalId} onChange={(e) => setExternalId(e.target.value)} data-testid="inv-external" className="wiz-input">
                    <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                    {externals.map(x => <option key={x.id} value={x.id} className="bg-[#0A0A0A]">{x.full_name}</option>)}
                  </select>
                </Field>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-3">Leistungen hinzufügen</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                  {(services || []).map(p => (
                    <button key={p.id || p.label} onClick={() => addPreset(p)} data-testid={`preset-${p.label}`}
                      className="text-left border border-white/10 hover:border-[#ff0033]/40 p-3 transition-colors">
                      <div className="text-xs text-white font-medium">{p.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{p.price > 0 ? `${p.price} € / ${p.unit}` : `pro ${p.unit}`}</div>
                    </button>
                  ))}
                </div>
                <button onClick={addCustom} data-testid="add-custom-item" className="text-[10px] tracking-[0.25em] uppercase text-[#ff0033] font-bold hover:text-white">
                  + Freie Position
                </button>
              </div>

              {items.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_60px_50px_80px_80px_30px] gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold px-2">
                    <div>Beschreibung</div><div>Menge</div><div>EH</div><div>Einzelpreis</div><div className="text-right">Summe</div><div />
                  </div>
                  {items.map((it, i) => (
                    <div key={i} className="grid grid-cols-[1fr_60px_50px_80px_80px_30px] gap-2 items-center bg-[#0F0F0F] border border-white/[0.06] p-2">
                      <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} className="bg-transparent text-sm text-white outline-none px-2 py-1.5" />
                      <input type="number" step="0.5" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="bg-transparent text-sm text-white outline-none border border-white/10 px-2 py-1.5" />
                      <input value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="bg-transparent text-sm text-white outline-none border border-white/10 px-2 py-1.5" />
                      <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="bg-transparent text-sm text-white outline-none border border-white/10 px-2 py-1.5" />
                      <div className="text-sm text-white text-right pr-1">{formatMoney(Number(it.quantity) * Number(it.unit_price))}</div>
                      <button onClick={() => removeItem(i)} className="text-zinc-500 hover:text-[#ff0033]"><Trash2 size={13} /></button>
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <Field label="USt.-Satz (%)">
                      <input type="number" step="1" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="inv-taxrate" className="wiz-input" placeholder="0 = §19 Kleinunternehmer" />
                    </Field>
                    <div className="text-right pt-6">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Zwischensumme: <span className="text-white">{formatMoney(subtotal)}</span></div>
                      {taxRate > 0 && <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">USt.: <span className="text-white">{formatMoney(tax_amount)}</span></div>}
                      <div className="font-display font-black uppercase text-xl text-[#ff0033] mt-2">Total {formatMoney(total)}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Rechnungsdatum *"><input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="wiz-input" /></Field>
                <Field label="Leistungsdatum"><input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} className="wiz-input" /></Field>
                <Field label="Fällig am"><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="wiz-input" /></Field>
              </div>
              <Field label="Zusatz-Notiz auf der Rechnung (optional)">
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="wiz-input resize-none" placeholder="z.B. Betreff / Bezug zum Projekt" />
              </Field>
              <div className="bg-[#0F0F0F] border border-white/[0.06] p-4 space-y-2">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Zusammenfassung</div>
                <div className="text-sm text-white">{items.length} Position(en) · {formatMoney(total)}</div>
                <div className="text-xs text-zinc-500">Wird als PDF erstellt, im CRM archiviert und direkt heruntergeladen. Firmen-Daten werden zum Zeitpunkt der Erstellung eingefroren.</div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/[0.06]">
            <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">
              {step > 1 ? "← Zurück" : "Abbrechen"}
            </button>
            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={(step === 1 && !partySelected) || (step === 2 && items.length === 0)} data-testid="inv-next"
                className="bg-white text-black px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:bg-[#ff0033] hover:text-white disabled:opacity-40">
                Weiter →
              </button>
            ) : (
              <button onClick={submit} disabled={busy} data-testid="inv-submit"
                className="bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_24px_rgba(255,0,51,0.5)] disabled:opacity-50">
                {busy ? "Erstelle…" : "Rechnung erstellen"}
              </button>
            )}
          </div>
        </div>
        <style>{`
          .wiz-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
          .wiz-input:focus { border-color: rgba(255,0,51,0.5); }
        `}</style>
      </div>
    </div>
  );
};

// ─── Eingangsrechnung hochladen ─────────────────────────
const IncomingUpload = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [invNumber, setInvNumber] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState("");
  const [taxAmount, setTaxAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Bitte PDF/Bild auswählen"); return; }
    if (!invNumber) { toast.error("Rechnungsnummer angeben"); return; }
    setBusy(true);
    try {
      const subtotal = Number(total) - Number(taxAmount || 0);
      await createIncomingInvoice({ file, invoice_number: invNumber, invoice_date: date, notes, total: Number(total), tax_amount: Number(taxAmount || 0), subtotal });
      toast.success("Eingangsrechnung archiviert");
      onDone();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-black uppercase text-lg">Eingangsrechnung archivieren</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="PDF / Bild *">
            <input required type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0])} data-testid="upload-file" className="wiz-input" />
          </Field>
          <Field label="Rechnungsnummer * (vom Lieferanten)">
            <input required value={invNumber} onChange={(e) => setInvNumber(e.target.value)} data-testid="upload-invnr" className="wiz-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Rechnungsdatum"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="wiz-input" /></Field>
            <Field label="Netto+USt (Total)"><input type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} className="wiz-input" /></Field>
            <Field label="Enth. USt."><input type="number" step="0.01" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} className="wiz-input" /></Field>
          </div>
          <Field label="Notiz">
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="wiz-input resize-none" />
          </Field>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/[0.06]">
            <button type="button" onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">Abbrechen</button>
            <button type="submit" disabled={busy} data-testid="upload-submit" className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase disabled:opacity-50">
              {busy ? "Speichere…" : "Archivieren"}
            </button>
          </div>
        </form>
        <style>{`
          .wiz-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
          .wiz-input:focus { border-color: rgba(255,0,51,0.5); }
        `}</style>
      </div>
    </div>
  );
};

// ─── Settings Modal: Firmen-Daten für zukünftige Rechnungen ─
const SettingsModal = ({ onClose }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchInvoiceSettings().then(setS).catch(e => toast.error(e.message)); }, []);
  if (!s) return null;
  const set = (k) => (e) => setS(prev => ({ ...prev, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { id: _id, singleton: _s, updated_at: _u, ...patch } = s;
      await updateInvoiceSettings(patch);
      toast.success("Firmendaten gespeichert — gelten für zukünftige Rechnungen");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Buchhaltung</div>
            <div className="font-display font-black uppercase text-lg mt-1">Firmen-Daten</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-xs text-zinc-500 italic">Änderungen wirken nur auf zukünftige Rechnungen. Bereits erstellte Rechnungen bleiben unverändert.</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Firmenname *"><input value={s.company_name || ""} onChange={set("company_name")} className="wiz-input" /></Field>
            <Field label="Rechtsform"><input value={s.legal_form || ""} onChange={set("legal_form")} className="wiz-input" /></Field>
            <Field label="Inhaber / Ansprechpartner"><input value={s.owner_name || ""} onChange={set("owner_name")} className="wiz-input" /></Field>
            <Field label="Straße + Nr."><input value={s.address_street || ""} onChange={set("address_street")} className="wiz-input" /></Field>
            <Field label="PLZ"><input value={s.address_zip || ""} onChange={set("address_zip")} className="wiz-input" /></Field>
            <Field label="Stadt"><input value={s.address_city || ""} onChange={set("address_city")} className="wiz-input" /></Field>
            <Field label="E-Mail"><input value={s.email || ""} onChange={set("email")} className="wiz-input" /></Field>
            <Field label="Telefon"><input value={s.phone || ""} onChange={set("phone")} className="wiz-input" /></Field>
            <Field label="Webseite"><input value={s.website || ""} onChange={set("website")} className="wiz-input" /></Field>
            <Field label="Steuernummer"><input value={s.tax_id || ""} onChange={set("tax_id")} className="wiz-input" /></Field>
            <Field label="USt-IdNr. (falls vorhanden)"><input value={s.vat_id || ""} onChange={set("vat_id")} className="wiz-input" /></Field>
            <Field label="Standard-USt-Satz (%)"><input type="number" value={s.default_tax_rate || 0} onChange={set("default_tax_rate")} className="wiz-input" /></Field>
            <Field label="Bank"><input value={s.bank_name || ""} onChange={set("bank_name")} className="wiz-input" /></Field>
            <Field label="IBAN"><input value={s.iban || ""} onChange={set("iban")} className="wiz-input" /></Field>
            <Field label="BIC"><input value={s.bic || ""} onChange={set("bic")} className="wiz-input" /></Field>
            <div className="md:col-span-2 flex items-center gap-3 p-3 border border-white/10">
              <input type="checkbox" checked={!!s.small_business} onChange={set("small_business")} className="w-5 h-5 accent-[#ff0033]" />
              <span className="text-sm text-white">Kleinunternehmer nach §19 UStG (keine USt. ausweisen)</span>
            </div>
          </div>
          <Field label="Standard-Fußzeile / Zahlungshinweis">
            <textarea rows={3} value={s.invoice_footer || ""} onChange={set("invoice_footer")} className="wiz-input resize-none" />
          </Field>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/[0.06]">
            <button onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">Abbrechen</button>
            <button onClick={save} disabled={busy} data-testid="settings-save" className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase disabled:opacity-50">
              {busy ? "Speichere…" : "Speichern"}
            </button>
          </div>
        </div>
        <style>{`
          .wiz-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
          .wiz-input:focus { border-color: rgba(255,0,51,0.5); }
        `}</style>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">{label}</span>
    {children}
  </label>
);

// ─── ServicesModal — Leistungen verwalten (add/edit/delete) ───
const ServicesModal = ({ services, onClose, onChange }) => {
  const [items, setItems] = useState(services);
  useEffect(() => { setItems(services); }, [services]);
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("Stück");
  const [newPrice, setNewPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const addNew = async () => {
    if (!newLabel.trim()) { toast.error("Bezeichnung ist Pflicht"); return; }
    setBusy(true);
    try {
      await createService({ label: newLabel.trim(), unit: newUnit || "Stück", price: Number(newPrice || 0), sort_order: (items[items.length - 1]?.sort_order || 0) + 1 });
      setNewLabel(""); setNewUnit("Stück"); setNewPrice("");
      await onChange();
      toast.success("Leistung angelegt");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const patch = async (id, field, value) => {
    try {
      await updateService(id, { [field]: field === "price" ? Number(value) : value });
      await onChange();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Leistung „${label}" wirklich löschen?`)) return;
    try { await deleteService(id); await onChange(); toast.success("Gelöscht"); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Katalog</div>
            <div className="font-display font-black uppercase text-lg mt-1">Leistungen verwalten</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-xs text-zinc-500">Diese Leistungen erscheinen im Rechnungs-Wizard als Klick-Presets. Änderungen wirken sofort für neue Rechnungen.</div>

          {/* Neue Leistung anlegen */}
          <div className="bg-[#0F0F0F] border border-white/[0.06] p-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-3">Neue Leistung</div>
            <div className="grid grid-cols-[1fr_80px_80px_auto] gap-2 items-end">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Bezeichnung</div>
                <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="svc-new-label" placeholder="z.B. Podcast-Editing" className="w-full bg-transparent border border-white/10 px-2 py-2 text-sm text-white outline-none focus:border-[#ff0033]/50" />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Einheit</div>
                <input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} data-testid="svc-new-unit" className="w-full bg-transparent border border-white/10 px-2 py-2 text-sm text-white outline-none focus:border-[#ff0033]/50" />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Preis €</div>
                <input type="number" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="svc-new-price" className="w-full bg-transparent border border-white/10 px-2 py-2 text-sm text-white outline-none focus:border-[#ff0033]/50" />
              </div>
              <button onClick={addNew} disabled={busy} data-testid="svc-new-submit" className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase h-[38px] disabled:opacity-50">
                <Plus size={13} className="inline mr-1" />ADD
              </button>
            </div>
          </div>

          {/* Bestehende Leistungen */}
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-3">Bestehende Leistungen ({items.length})</div>
            {items.length === 0 ? (
              <div className="text-sm text-zinc-500 italic">Noch keine Leistungen. Lege oben deine erste an.</div>
            ) : (
              <div className="space-y-2">
                {items.map(s => (
                  <div key={s.id} data-testid={`svc-${s.id}`} className="grid grid-cols-[1fr_80px_80px_30px] gap-2 items-center bg-[#0F0F0F] border border-white/[0.06] p-2">
                    <input defaultValue={s.label} onBlur={(e) => e.target.value !== s.label && patch(s.id, "label", e.target.value)} className="bg-transparent text-sm text-white outline-none px-2 py-1.5" />
                    <input defaultValue={s.unit} onBlur={(e) => e.target.value !== s.unit && patch(s.id, "unit", e.target.value)} className="bg-transparent text-sm text-white outline-none border border-white/10 px-2 py-1.5" />
                    <input type="number" step="0.01" defaultValue={s.price} onBlur={(e) => Number(e.target.value) !== Number(s.price) && patch(s.id, "price", e.target.value)} className="bg-transparent text-sm text-white outline-none border border-white/10 px-2 py-1.5" />
                    <button onClick={() => remove(s.id, s.label)} data-testid={`svc-delete-${s.id}`} title="Löschen" className="text-zinc-500 hover:text-[#ff0033] flex justify-center"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end pt-3 border-t border-white/[0.06]">
            <button onClick={onClose} className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase">
              Fertig
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
